import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import React from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';

const ProtectedRoutes = ({ children }) => {
    const {
        user,
        loading,
        hasProfile,
        isUsernameAutoGenerated,
        profileLoading,
    } = useAuth();
    const location = useLocation();

    // Wait for both auth and profile checks to complete before rendering.
    // hasProfile === null means the profile check hasn't resolved yet (Task #118).
    // isUsernameAutoGenerated === null means it hasn't resolved yet either.
    if (
        loading ||
        profileLoading ||
        (user && user.emailVerified && hasProfile === null) ||
        (user &&
            user.emailVerified &&
            hasProfile === true &&
            isUsernameAutoGenerated === null)
    ) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (!user) {
        // Capture the current path and pass it as a redirect parameter
        const redirectTo = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                replace
            />
        );
    }

    // If user doesn't have email verified, redirect to login
    if (!user.emailVerified) return <Navigate to="/login" replace />;

    // If no MongoDB profile and not on create-profile page, redirect to create-profile
    if (hasProfile === false && location.pathname !== '/create-profile') {
        return <Navigate to="/create-profile" replace />;
    }

    // If has profile but on create-profile page, redirect to dashboard
    if (hasProfile === true && location.pathname === '/create-profile') {
        return <Navigate to="/dashboard" replace />;
    }

    // If username is auto-generated, gate user to /setup-username
    if (
        isUsernameAutoGenerated === true &&
        location.pathname !== '/setup-username'
    ) {
        return <Navigate to="/setup-username" replace />;
    }

    // If username is not auto-generated, don't allow access to /setup-username
    if (
        isUsernameAutoGenerated === false &&
        location.pathname === '/setup-username'
    ) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

ProtectedRoutes.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ProtectedRoutes;
